import React, { useState } from 'react';
import theme from './RoomInfo.module.scss';
import { SimpleCard } from '@ygtechnologies/bygr-frontend-component-library';
import { OptionalId, RoomDto } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonBar, ConfirmButton, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { LinkedUnitsModal } from './LinkedUnitsModal';
import { faCopy, faEdit, faHouse, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { t } from '@lingui/core/macro';
import { searchOptionGroups } from '@/services/options/option-groups';
import { createQueryHook, useGetProjectId } from '@/utils/hooks';
import { YesNoDialog } from '@/components/dialogs';
import RoomLabel from '@/components/RoomLabel';

export interface RoomInfoViewProps {
    room: OptionalId<RoomDto>;
    onEditClick?(): void;
    onDeleteClick?(room: RoomDto): void;
    onCopyClick?(room: RoomDto): void;
}

export const RoomInfoView: React.FC<RoomInfoViewProps> = ({ room, onEditClick, onDeleteClick, onCopyClick }) => {
    const projectId = useGetProjectId();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const { data: options } = createQueryHook(searchOptionGroups)({ projectId: projectId!, rooms: [room.id!] });
    return (
        <SimpleCard className={theme.roomInfoCard}>
            <div className={theme.topRow}>
                {room.icon && <FontAwesomeIcon size="2x" icon={['fal', room.icon]} />}
                <h2>
                    {room.name
                        ? <RoomLabel name={room.name} labelNote={room.labelNote} />
                        : <Trans>Room</Trans>}
                </h2>
            </div>

            <div className={theme.descriptionRow}>
                <div className={theme.labeledInput}>
                    <label>
                        <Trans>Internal description</Trans>
                    </label>
                    <div className={theme.viewField}>{room.sellerDescription || '-'}</div>
                </div>
                <div className={theme.labeledInput}>
                    <label>
                        <Trans>External description / legal notice</Trans>
                    </label>
                    <div className={theme.viewField}>{room.buyerDescription || '-'}</div>
                </div>
            </div>
            <ButtonBar>
                <ButtonBar.Right>
                    <Button variant="tertiary" onClick={() => setIsModalOpen(true)} icon={faHouse}>
                        <Trans>Show linked units</Trans>
                    </Button>
                    {onCopyClick && room && room.id && (
                        <Button variant="tertiary" onClick={() => setShowCopyDialog(true)} icon={faCopy}>
                            <Trans>Copy room</Trans>
                        </Button>
                    )}
                    {onDeleteClick && room && room.id && (
                        <SimpleTooltip
                            content={options && options.length > 0 ? t`Cannot delete room with linked options` : ''}>
                            <ConfirmButton
                                dialogTitle={<Trans>Delete room</Trans>}
                                dialogBody={<Trans>Are you sure you want to delete this room </Trans>}
                                disabled={(options?.length ?? 0) > 0}
                                icon={faTrashAlt}
                                variant="tertiary"
                                onConfirm={() => onDeleteClick(room as RoomDto)}>
                                <Trans>Delete room</Trans>
                            </ConfirmButton>
                        </SimpleTooltip>
                    )}
                    {onEditClick && (
                        <div className={theme.buttons}>
                            <Button variant="tertiary" onClick={onEditClick} icon={faEdit}>
                                <Trans>Edit</Trans>
                            </Button>
                        </div>
                    )}
                </ButtonBar.Right>
            </ButtonBar>
            <LinkedUnitsModal roomId={room.id} isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
            <YesNoDialog
                show={showCopyDialog}
                title={<Trans>Copy room</Trans>}
                body={<Trans>Do you want to copy {room.name}?</Trans>}
                onAccept={() => { onCopyClick?.(room as RoomDto); setShowCopyDialog(false); }}
                onClose={() => setShowCopyDialog(false)}
            />
        </SimpleCard>
    );
};
